import { createContext, useState, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import dayjs from 'dayjs';
import { extendProfileCompleteReminderTime, intialFilterState, updateFilterPreference } from "../../services/profile";
import { LOCALSTORAGE_FILTER_PREFERENCE_KEY, LOCALSTORAGE_TOKEN_KEY, PROFILE_REMINDER_EXTENSION_TIME_IN_DAYS, LOCALSTORAGE_HOMEPAGE_PAGENUMBER, LOCALSTORAGE_FAVOURITES_PAGENUMBER } from "../../services/Constants";
import { getLatestConfig } from '../../services/acknowledgement';


const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
	//IMPORTANT - any new state object here might be required to be reset on logout.
	const [auth, setAuthObj] = useState({});
	const [profile, setProfile] = useState({});
	const [favourites, setFavourites] = useState([]);
	const [reportedUsers, setReportedUsers] = useState([]);
	const [userRole, setUserRole] = useState('');
	const [isUserPaymentValid, setIsUserPaymentValid] = useState(false);
	const [profileCompleteReminderTime, setProfileCompleteReminderTime] = useState(new Date().toString());
	const [filterLoadedState, setFilterLoadedState] = useState({
		areFiltersLoaded: false,
		filters: intialFilterState()
	});
	// Latest configs
	const [latestTncVersion, setLatestTncVersion] = useState('');
	const [tncContent, setTncContent] = useState('');
	// User specific values mirroring the configs
	const [userSignedTncVersion, setUserSignedTncVersion] = useState(null);
	const [shouldUserSignTnc, setShouldUserSignTnc] = useState(null)
	const [userSignedDeclarationTime, setUserSignedDeclarationTime] = useState(null);
	const [shouldUserProvideDeclaration, setShouldUserProvideDeclaration] = useState(null);
	const [userViewedPreviewIntroTime, setUserViewedPreviewIntroTime] = useState(null);
	const [shouldUserViewPreviewIntro, setShouldUserViewPreviewIntro] = useState(null);


	useEffect(() => {
		const fetchConfigs = async () => {
			try {
				const response = await getLatestConfig();
				const config = response?.data?.getLatestConfigs;
				if (config) {
					setLatestTncVersion(config.latestTnCVersion);
					setTncContent(config.tncContent);
				} else {
					throw new Error("config rec'd as empty from getlatest configs.")
				}
			} catch (error) {
				console.error('Failed to fetch latest config', error);
			}
		};
	
		fetchConfigs();
	}, []);

	useEffect(() => {

		//null implies not set
		if (latestTncVersion !== null && userSignedTncVersion !== null) {
			setShouldUserSignTnc(userSignedTncVersion < latestTncVersion);
		}
	}, [latestTncVersion, userSignedTncVersion]);

	useEffect(() => {
		// null implies its not set
		if (userSignedDeclarationTime !== null) {
			// API returns "" if user does not have a declaration
			setShouldUserProvideDeclaration(userSignedDeclarationTime === "");
		}
	}, [userSignedDeclarationTime]);

	useEffect(() => {
		// null implies its not set
		if (userViewedPreviewIntroTime !== null) {
			// API returns "" if user has not viewed preview
			setShouldUserViewPreviewIntro(userViewedPreviewIntroTime === "");
		}
	}, [userViewedPreviewIntroTime]);	

	const loadFilters = (newFilterState) => {
		setFilterLoadedState({
			areFiltersLoaded: true, 
			filters: newFilterState
		});
	}

	const updateFilterState = (newFilterState) => {
		setFilterLoadedState({
			...filterLoadedState, 
			filters: newFilterState
		});
	}

	const addReportsToState = (newIds) => {
		var finalReportedList = reportedUsers.concat(newIds);
		setReportedUsers(finalReportedList)
	}

	const addFavouritesToState = (listOfIds) => {
		let tempFavourites = favourites.concat(listOfIds);
		let uniqueFavourites = tempFavourites.filter((fav, index) => {
			return tempFavourites.findIndex((temp) => temp === fav) === index;
		});
		setFavourites(uniqueFavourites);
	};

	const removeFavouriteFromState = (id) => {
		let tempFavourites = favourites.filter((favourite) => favourite !== id);
		setFavourites(tempFavourites);
	};

	const setAuth = (authObject) => {
		setAuthObj(authObject);
		localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, JSON.stringify(authObject.token));
	};

	const getToken = () => {
		if (!auth?.token) return null;
		let decodedJwt = jwt_decode(auth.token);
		const isExpired = dayjs.unix(decodedJwt.exp).diff(dayjs()) < 1;
		if (isExpired) {
			logout();
			return null;
		}
		return auth;
	};
	const updateUserPreference = async () => {
		try {
			let auth = getToken();	
			const filterPreference = localStorage.getItem(LOCALSTORAGE_FILTER_PREFERENCE_KEY)?.replace(/"/g, '\\"');

			if(auth?.token && filterPreference && filterPreference.length > 0) {
				let response = await updateFilterPreference (auth?.token, filterPreference);
				if (!response.data?.updateUserFilterPreference) {
					console.error('error in updating filter preference, gql response', {response, filterPreference});
				}
			} else {
				console.error("Update preference skipped as token expired or filterPreference string is empty");
			}
		} catch (error) {
			console.error('error in updating filter preference', error);
		}		
	};

	const extendProfileCompleteReminder = async () => {
		try {
			let auth = getToken();
			let response = await extendProfileCompleteReminderTime(auth?.token);
			if (!response.data?.extendProfileCompleteReminder) {
				console.error('error in extending profile update reminder, gql response', {response});
			}
			//Irrespective of the success of the api call, we need to extend the reminderTime
			const newReminderTime = new Date();
			newReminderTime.setDate(newReminderTime.getDate() + PROFILE_REMINDER_EXTENSION_TIME_IN_DAYS);
			
			var newReminderTimeString = response.data?.extendProfileCompleteReminder ?? newReminderTime.toString();
			setProfileCompleteReminderTime(newReminderTimeString);
		} catch (error) {
			console.error('error in extending profile update reminder', error);
		}
	}

	const logout = async () => {
		await updateUserPreference()
		setAuthObj({});
		localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
		localStorage.removeItem(LOCALSTORAGE_FILTER_PREFERENCE_KEY);
		setProfile({});
		setUserRole('');
		setIsUserPaymentValid(false);
		setProfileCompleteReminderTime(new Date().toString());
		setReportedUsers([]);
		setFilterLoadedState({
			areFiltersLoaded: false,
			filters: intialFilterState()
		});
		setFavourites([]);
		setUserSignedTncVersion(null);
		setUserSignedDeclarationTime(null);
		setShouldUserSignTnc(null);
		setShouldUserProvideDeclaration(null);
		setUserViewedPreviewIntroTime(null);
		setShouldUserViewPreviewIntro(null);
		sessionStorage.removeItem(LOCALSTORAGE_HOMEPAGE_PAGENUMBER);
		sessionStorage.removeItem(LOCALSTORAGE_FAVOURITES_PAGENUMBER);
	};

	return (
		<AuthContext.Provider
			value={{
				setAuth,
				profile,
				setProfile,
				logout,
				getToken,
				userRole,
				setUserRole,
				favourites,
				addFavouritesToState,
				removeFavouriteFromState,
				isUserPaymentValid,
				setIsUserPaymentValid,
				reportedUsers, 
				addReportsToState,
				filterState: filterLoadedState.filters,
				setFilterState: updateFilterState,
				areFiltersLoaded: filterLoadedState.areFiltersLoaded,
				loadFilters,
				extendProfileCompleteReminder,
				setProfileCompleteReminderTime,
				profileCompleteReminderTime,
				tncContent,
				shouldUserSignTnc,
				setUserSignedTncVersion,
				latestTncVersion,
				shouldUserProvideDeclaration,
				setUserSignedDeclarationTime,
				shouldUserViewPreviewIntro,
				setUserViewedPreviewIntroTime
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthContext;
